import { InvoiceStatus } from '@nxsys/constants/AppEnums';
import { useAsync } from '@nxsys/hooks/useAsync';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { getAgencyTimeSheetListApi } from '@nxsys/services/api';
import {
  createCreditNoteApi,
  createInvoiceApi,
  creditInvoiceApi,
  downloadAllInvoiceApi,
  downloadInvoiceApi,
  getInvoiceByIdApi,
  getInvoiceListApi,
  markAllAsPaidInvoiceApi,
  markAsPaidInvoiceApi,
  previewCreditNoteByIdApi,
  previewInvoiceByIdApi,
  raiseInvoiceApi,
  sendMailBureauInvoiceApi,
  sendMailInvoiceApi,
  sendMailRaiseInvoiceApi,
  updateCreditNoteByIdApi,
  updateInvoiceByIdApi,
} from '@nxsys/services/api/invoice';
import { AppState } from '@nxsys/store';
import { roles } from '@nxsys/store/constants';
import {
  CreateCreditNoteParams,
  CreateCreditNoteResponse,
  CreateInvoiceParams,
  CreateInvoiceResponse,
  DownloadInvoiceParams,
  DownloadInvoiceResponse,
  GetInvoiceByIdParams,
  GetInvoiceByIdResponse,
  GetInvoiceListParams,
  GetInvoiceListResponse,
  PreviewCreditNoteByIdParams,
  PreviewCreditNoteByIdResponse,
  PreviewInvoiceByIdParams,
  PreviewInvoiceByIdResponse,
  RaiseInvoiceParams,
  RaiseInvoiceResponse,
  SendEmailInvoiceParams,
  SendEmailInvoiceResponse,
  SendEmailRaiseInvoiceParams,
  SendEmailRaiseInvoiceResponse,
  UpdateInvoiceByIdParams,
  UpdateInvoiceByIdResponse,
} from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';

export const useGetInvoiceList = () => {
  const { isBureau, isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  const { value, loading, error, run } = useAsync<
    GetInvoiceListResponse,
    GetInvoiceListParams
  >(
    user?.role === roles.agency
      ? (params: GetInvoiceListParams) =>
        getAgencyTimeSheetListApi(user?.agencyId?.id ?? '', {
          ...params,
          invoiceStatus: InvoiceStatus.INVOICED,
        }) as Promise<GetInvoiceListResponse>
      : getInvoiceListApi,
  );

  const _run = (params: GetInvoiceListParams) =>
    run({
      ...params,
      ...(!isBureau ? { isNexus: true } : {}),
      ...(isBureauEmployer ? { companyRefs: user?.companyUserIds ?? [] } : {}),
    });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  const refetch = () => _run({ ...value?.lastArgs });

  return {
    value,
    loading,
    error,
    run: _run,
    refetch,
  };
};

export const useCreateInvoice = () => {
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const { value, loading, error, run } = useAsync<
    CreateInvoiceResponse,
    CreateInvoiceParams
  >((params) => createInvoiceApi(params, { isBureau }));

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetInvoice = () => {
  const { value, loading, error, run } = useAsync<
    GetInvoiceByIdResponse,
    GetInvoiceByIdParams
  >(getInvoiceByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateInvoice = () => {
  const { messages } = useTypedIntl();
  const { value, loading, error, run } = useAsync<
    UpdateInvoiceByIdResponse,
    UpdateInvoiceByIdParams
  >(updateInvoiceByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    } else if (value) {
      showMessage({
        type: 'success',
        content: messages['invoices.updateInvoiceSuccess'],
      });
    }
  }, [error, value]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const usePreviewInvoice = () => {
  const { value, loading, error, run } = useAsync<
    PreviewInvoiceByIdResponse,
    PreviewInvoiceByIdParams
  >(previewInvoiceByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSendInvoice = () => {
  const { messages } = useTypedIntl();
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const { value, loading, error, run } = useAsync<
    SendEmailInvoiceResponse,
    SendEmailInvoiceParams
  >(isBureau ? sendMailBureauInvoiceApi : sendMailInvoiceApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    } else if (value?.success) {
      showMessage({
        type: 'info',
        content: messages['invoices.sendEmailProcessing'],
      });
    }
  }, [error, value]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSendMailRaiseInvoice = () => {
  const { messages } = useTypedIntl();

  const { value, loading, error, run } = useAsync<
    SendEmailRaiseInvoiceResponse,
    SendEmailRaiseInvoiceParams
  >(sendMailRaiseInvoiceApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    } else if (value?.success) {
      showMessage({
        type: 'info',
        content: messages['invoices.sendMailSuccess'],
      });
    }
  }, [error, value]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useDownloadInvoice = () => {
  const { value, loading, error, run } = useAsync<
    DownloadInvoiceResponse,
    DownloadInvoiceParams
  >(downloadInvoiceApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useDownloadAllInvoice = () => {
  const { value, loading, error, run } = useAsync(downloadAllInvoiceApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCreditInvoice = () => {
  const { value, loading, error, run } = useAsync(creditInvoiceApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useMarkAsPaidInvoice = () => {
  const { value, loading, error, run } = useAsync(markAsPaidInvoiceApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useMarkAllAsPaidInvoice = () => {
  const { value, loading, error, run } = useAsync(markAllAsPaidInvoiceApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useRaiseInvoice = () => {
  const { isBureau } = useSelector((state: AppState) => state.appStore);
  const { value, loading, error, run } = useAsync<
    RaiseInvoiceResponse,
    RaiseInvoiceParams
  >((params) => raiseInvoiceApi(params, { isBureau }));

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetCreditNoteList = () => {
  const { isBureau, isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { value, loading, error, run } = useAsync<
    GetInvoiceListResponse,
    GetInvoiceListParams
  >(
    user?.role === roles.agency
      ? (params: GetInvoiceListParams) =>
        getAgencyTimeSheetListApi(user?.agencyId?.id ?? '', {
          ...params,
          invoiceStatus: InvoiceStatus.CREDITED,
        }) as Promise<GetInvoiceListResponse>
      : getInvoiceListApi,
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  const _run = (params: GetInvoiceListParams) =>
    run({
      ...params,
      invoiceStatus: InvoiceStatus.CREDITED,
      ...(!isBureau ? { isNexus: true } : {}),
      ...(isBureauEmployer ? { companyRefs: user?.companyUserIds ?? [] } : {}),
    });

  const refetch = () => _run({ ...value?.lastArgs });

  return {
    value,
    loading,
    error,
    run: _run,
    refetch,
  };
};

export const useCreateCreditNote = () => {
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const { value, loading, error, run } = useAsync<
    CreateCreditNoteResponse,
    CreateCreditNoteParams
  >((params) => createCreditNoteApi(params, { isBureau }));

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const usePreviewCreditNote = () => {
  const { value, loading, error, run } = useAsync<
    PreviewCreditNoteByIdResponse,
    PreviewCreditNoteByIdParams
  >(previewCreditNoteByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useDownloadCreditNote = () => {
  const { value, loading, error, run } = useAsync<
    DownloadInvoiceResponse,
    DownloadInvoiceParams
  >(downloadInvoiceApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useDownloadAllCreditNote = () => {
  const { value, loading, error, run } = useAsync(downloadAllInvoiceApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateCreditNote = () => {
  const { messages } = useTypedIntl();
  const { value, loading, error, run } = useAsync<
    UpdateInvoiceByIdResponse,
    UpdateInvoiceByIdParams
  >(updateCreditNoteByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    } else if (value) {
      showMessage({
        type: 'success',
        content: messages['invoices.updateCreditNoteSuccess'],
      });
    }
  }, [error, value]);

  return {
    value,
    loading,
    error,
    run,
  };
};
